import { useEffect, useRef, useState, useCallback } from 'react';
import { useLottery } from './hooks/useLottery';

function useTheme() {
  const [isDark, setIsDark] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('lottery-theme');
      if (saved) return saved === 'dark';
      return true;
    }
    return true;
  });

  useEffect(() => {
    const html = document.documentElement;
    if (isDark) {
      html.classList.remove('light');
      localStorage.setItem('lottery-theme', 'dark');
    } else {
      html.classList.add('light');
      localStorage.setItem('lottery-theme', 'light');
    }
  }, [isDark]);

  const toggle = useCallback(() => setIsDark((d) => !d), []);

  return { isDark, toggle };
}

function useLotterySound() {
  const ctxRef = useRef<AudioContext | null>(null);

  // Create or resume AudioContext — must happen inside user gesture on mobile
  const ensureCtx = useCallback(() => {
    if (!ctxRef.current) {
      const AC = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (AC) {
        ctxRef.current = new AC();
      }
    }
    if (ctxRef.current?.state === 'suspended') {
      ctxRef.current.resume();
    }
    return ctxRef.current;
  }, []);

  // Eagerly unlock audio on first user interaction anywhere on the page
  useEffect(() => {
    const unlock = () => {
      ensureCtx();
    };
    const events = ['touchstart', 'touchend', 'click'];
    events.forEach((e) => document.addEventListener(e, unlock, { once: true }));
    return () => events.forEach((e) => document.removeEventListener(e, unlock));
  }, [ensureCtx]);

  // Mechanical slot-machine tick / clack on every roll
  const playTick = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    try {
      const t = ctx.currentTime;

      // Main mechanical clack — square wave for grit, fast sweep down
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'square';
      osc.frequency.setValueAtTime(800, t);
      osc.frequency.exponentialRampToValueAtTime(220, t + 0.035);
      gain.gain.setValueAtTime(0.10, t);
      gain.gain.exponentialRampToValueAtTime(0.001, t + 0.045);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(t);
      osc.stop(t + 0.045);

      // Sharp metallic click overlay
      const clickOsc = ctx.createOscillator();
      const clickGain = ctx.createGain();
      clickOsc.type = 'triangle';
      clickOsc.frequency.setValueAtTime(2000, t);
      clickOsc.frequency.exponentialRampToValueAtTime(500, t + 0.02);
      clickGain.gain.setValueAtTime(0.05, t);
      clickGain.gain.exponentialRampToValueAtTime(0.001, t + 0.025);
      clickOsc.connect(clickGain);
      clickGain.connect(ctx.destination);
      clickOsc.start(t);
      clickOsc.stop(t + 0.025);
    } catch {
      // Ignore audio errors
    }
  }, [ensureCtx]);

  // Winning celebration — bright arpeggio + chime
  const playWin = useCallback(() => {
    const ctx = ensureCtx();
    if (!ctx) return;
    try {
      const t = ctx.currentTime;
      const baseFreq = 523.25; // C5

      // C5 - E5 - G5 - C6 arpeggio (sine, soft but bright)
      const ratios = [1, 1.2599, 1.4983, 2];
      const delays = [0, 0.07, 0.14, 0.21];

      ratios.forEach((ratio, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.frequency.setValueAtTime(baseFreq * ratio, t + delays[i]);
        gain.gain.setValueAtTime(0.14, t + delays[i]);
        gain.gain.exponentialRampToValueAtTime(0.001, t + delays[i] + 0.45);
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.start(t + delays[i]);
        osc.stop(t + delays[i] + 0.45);
      });

      // Final sustaining chime on C6
      const chime = ctx.createOscillator();
      const chimeGain = ctx.createGain();
      chime.type = 'sine';
      chime.frequency.setValueAtTime(baseFreq * 2, t + 0.28);
      chimeGain.gain.setValueAtTime(0.12, t + 0.28);
      chimeGain.gain.exponentialRampToValueAtTime(0.001, t + 0.28 + 0.7);
      chime.connect(chimeGain);
      chimeGain.connect(ctx.destination);
      chime.start(t + 0.28);
      chime.stop(t + 0.28 + 0.7);

      // Short noise burst for "sparkle" texture
      const bufferSize = ctx.sampleRate * 0.06;
      const noiseBuffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = noiseBuffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = (Math.random() * 2 - 1) * (1 - i / bufferSize);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      const noiseGain = ctx.createGain();
      const hp = ctx.createBiquadFilter();
      hp.type = 'highpass';
      hp.frequency.value = 3000;
      noiseGain.gain.setValueAtTime(0.04, t + 0.15);
      noiseGain.gain.exponentialRampToValueAtTime(0.001, t + 0.15 + 0.12);
      noise.connect(hp);
      hp.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noise.start(t + 0.15);
      noise.stop(t + 0.15 + 0.12);
    } catch {
      // Ignore audio errors
    }
  }, [ensureCtx]);

  return { init: ensureCtx, playTick, playWin };
}

export default function Home() {
  const {
    t,
    currentLang,
    entries,
    history,
    isDrawing,
    drawDisplay,
    drawStatus,
    drawPhase,
    totalWeight,
    validEntries,
    addEntry,
    removeEntry,
    updateEntry,
    resetDefaults,
    startDraw,
    changeLang,
    getProbability,
    langList,
  } = useLottery();

  const { isDark, toggle: toggleTheme } = useTheme();
  const { init: initSound, playTick, playWin } = useLotterySound();
  const [isShaking, setIsShaking] = useState(false);
  const historyEndRef = useRef<HTMLDivElement>(null);

  // Trigger shake animation when result lands
  useEffect(() => {
    if (drawPhase === 'landed') {
      setIsShaking(true);
      const timer = setTimeout(() => setIsShaking(false), 600);
      return () => clearTimeout(timer);
    }
  }, [drawPhase]);

  useEffect(() => {
    if (history.length > 0 && historyEndRef.current) {
      historyEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [history.length]);

  const isFormValid = validEntries.length > 0;
  const btnLabel = isDrawing ? t.drawing : history.length > 0 ? t.drawAgain : t.drawStart;

  const handleStartDraw = useCallback(async () => {
    initSound();
    await startDraw((_value, progress) => {
      if (progress < 1) {
        playTick();
      }
    });
    // Draw finished — play winning celebration
    playWin();
  }, [initSound, playTick, playWin, startDraw]);

  return (
    <div className="min-h-screen bg-foreground-950 py-8 md:py-12 px-4 relative overflow-hidden">
      {/* Tech grid background */}
      <div className="absolute inset-0 tech-grid opacity-50 pointer-events-none" />

      {/* Ambient glows — both green to match reference */}
      <div className="ambient-glow w-[400px] h-[400px] bg-primary-500/15 -top-40 -right-40" />
      <div className="ambient-glow w-[350px] h-[350px] bg-accent-500/10 bottom-20 -left-32" />

      <div className="max-w-[680px] mx-auto relative z-10">
        {/* Header row */}
        <div className="flex items-start justify-between mb-5 gap-4">
          <div className="flex flex-wrap gap-2 mb-5">
            {langList.map((code) => (
              <button
                key={code}
                type="button"
                className={`lang-pill ${code === currentLang ? 'active' : ''}`}
                onClick={() => changeLang(code)}
              >
                {LANG_LABELS[code]}
              </button>
            ))}
          </div>
          <button
            type="button"
            className="theme-toggle"
            title={isDark ? 'Switch to bright mode' : 'Switch to dark mode'}
            aria-label={isDark ? 'Switch to bright mode' : 'Switch to dark mode'}
            onClick={toggleTheme}
          >
            <i className="ri-sun-line icon-sun" />
            <i className="ri-moon-line icon-moon" />
          </button>
        </div>

        {/* Title section */}
        <div className="mb-6 animate-fade-in">
          <h1 className="text-xl md:text-2xl font-bold text-background-50 tracking-tight mb-1">
            {t.title}
          </h1>
          <p className="text-sm text-background-400 mb-3">{t.subtitle}</p>
          <p className="text-[13px] text-background-300 leading-relaxed">{t.desc}</p>
        </div>

        {/* Entries card */}
        <div className="lottery-card p-4 md:p-5 mb-5 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-sm font-semibold text-background-200">{t.sectionLabel}</h2>
            <span className="text-xs text-background-400 font-mono tracking-wider uppercase">
              {t.totalWeight(totalWeight % 1 === 0 ? totalWeight : totalWeight.toFixed(1))}
            </span>
          </div>

          <div className="flex flex-col">
            {entries.map((entry) => {
              const prob = getProbability(entry);
              return (
                <div
                  key={entry.id}
                  className="flex items-center gap-3 py-2 px-3 rounded-lg transition-all duration-300 hover:bg-background-50/5"
                >
                  <div className="flex flex-col gap-1 w-[100px] flex-shrink-0">
                    <label className="text-[11px] text-background-400 font-medium tracking-wider uppercase">
                      {t.numberLabel}
                    </label>
                    <input
                      type="text"
                      className="entry-input"
                      value={entry.number}
                      onChange={(e) => updateEntry(entry.id, 'number', e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-1 w-[90px] flex-shrink-0">
                    <label className="text-[11px] text-background-400 font-medium tracking-wider uppercase">
                      {t.weightLabel}
                    </label>
                    <input
                      type="number"
                      min={0}
                      step={1}
                      className="entry-input"
                      value={entry.weight}
                      onChange={(e) => updateEntry(entry.id, 'weight', e.target.value)}
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-1 min-w-0 pt-[18px]">
                    <div className="prob-bar-track">
                      <div
                        className="prob-bar-fill"
                        style={{ width: `${prob.toFixed(1)}%` }}
                      />
                    </div>
                    <span className="text-[11px] text-background-400 font-mono tracking-wider">
                      {prob.toFixed(1)}%
                    </span>
                  </div>
                  <button
                    type="button"
                    title={t.removeTitle}
                    className="remove-btn mt-[18px]"
                    onClick={() => removeEntry(entry.id)}
                  >
                    <i className="ri-close-line text-sm" />
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-2 mt-4 pt-3 border-t border-background-50/10">
            <button type="button" className="btn-secondary text-xs md:text-sm" onClick={() => addEntry()}>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-add-line" />
              </span>
              {t.addEntry}
            </button>
            <button type="button" className="btn-secondary text-xs md:text-sm" onClick={resetDefaults}>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-restart-line" />
              </span>
              {t.resetDefaults}
            </button>
          </div>
        </div>

        {/* History section - always visible with 5 slots */}
        <div className="mb-5 animate-fade-in">
          <h3 className="text-sm font-semibold text-background-200 mb-3 tracking-wider uppercase font-mono">
            {t.historyTitle}
          </h3>
          <div className="flex flex-col gap-2 max-h-[260px] overflow-y-auto pr-1">
            {history.map((h, i) => {
              const timeStr = h.time.toLocaleTimeString(t.locale, { hour12: false });
              return (
                <div key={`real-${i}`} className="history-row">
                  <span className="text-background-400 text-xs">{t.historyItem(i + 1, timeStr)}</span>
                  <span className="text-background-50 font-bold">{h.number}</span>
                </div>
              );
            })}
            {Array.from({ length: Math.max(0, 5 - history.length) }).map((_, i) => (
              <div
                key={`placeholder-${i}`}
                className="flex items-center justify-between gap-3 border border-background-50/5 rounded-lg px-3 py-2.5 opacity-0 pointer-events-none select-none"
                aria-hidden="true"
              >
                <span className="text-xs font-mono tracking-wider text-background-500">
                  {t.historyItem(history.length + i + 1, '00:00:00')}
                </span>
                <span className="text-[11px] font-mono tracking-wider text-background-500">--</span>
              </div>
            ))}
            <div ref={historyEndRef} />
          </div>
        </div>

        {/* Draw stage */}
        <div className="animate-fade-in">
          <div className="lottery-card p-6 md:p-8 flex flex-col items-center gap-5 overflow-hidden relative">
            {/* Subtle top accent line */}
            <div className="absolute top-0 left-0 right-0 h-[1px] bg-primary-500/20" />

            {/* Display */}
            <div
              className={`relative w-[260px] h-[260px] md:w-[280px] md:h-[280px] rounded-xl overflow-hidden flex items-center justify-center text-center transition-all duration-300
                ${drawPhase === 'rolling' ? 'border-primary-500/30 bg-foreground-800 shadow-[0_0_30px_oklch(var(--primary-500)/0.08)]' : 'border border-background-50/10 bg-foreground-800'}
                ${drawPhase === 'landed' ? 'border-primary-500/50 shadow-[0_0_40px_oklch(var(--primary-500)/0.15)]' : ''}
                ${isShaking ? 'animate-win-shake' : ''}
              `}
            >
              {/* Inner ambient glow for landed state */}
              {drawPhase === 'landed' && (
                <div className="absolute inset-0 bg-primary-500/5 pointer-events-none" />
              )}
              <span
                className={`font-mono font-bold select-none relative z-[1] transition-colors duration-150 leading-none text-center inline-block
                  ${drawPhase === 'rolling' ? 'text-background-500' : 'text-background-50'}
                  ${drawPhase === 'landed' ? 'text-primary-400' : ''}
                `}
                style={{ fontSize: 'clamp(112px, 32vw, 140px)' }}
              >
                {drawDisplay}
              </span>
            </div>

            <p className="text-sm text-background-400 text-center min-h-[20px] font-mono tracking-wider">
              {drawStatus || t.drawIdleStatus}
            </p>

            <button
              type="button"
              className="btn-primary text-sm md:text-base px-8 py-3 transition-all duration-300"
              disabled={!isFormValid || isDrawing}
              onClick={handleStartDraw}
            >
              <span className="w-5 h-5 flex items-center justify-center">
                {isDrawing ? (
                  <i className="ri-loader-4-line animate-spin-slow text-base" />
                ) : (
                  <i className="ri-sparkling-line text-base" />
                )}
              </span>
              {btnLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

const LANG_LABELS: Record<string, string> = {
  tw: '繁體中文',
  cn: '简体中文',
  en: 'English',
  ja: '日本語',
  ko: '한국어',
};